
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {JournalResponse} from './JournalResponse';
import {LogLevelType} from '../core/LogLevelType';
import {FieldModelLogLevelType} from '../core/LogLevelType';

/**
 * JournalRequest struct
 */
class JournalRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!string=} module
   * @param {!LogLevelType=} logLevel
   * @param {!Date=} timeFrom
   * @param {!Date=} timeTo
   * @param {!string=} searchText
   * @param {!boolean=} caseSensitive
   * @constructor
   */
  constructor (parent = new Request(), module = '', logLevel = new LogLevelType(LogLevelType.ALL), timeFrom = new Date(0), timeTo = new Date(0), searchText = '', caseSensitive = false) {
    super()
    super.copy(parent)
    this.Module = module
    this.LogLevel = logLevel
    this.TimeFrom = timeFrom
    this.TimeTo = timeTo
    this.SearchText = searchText
    this.CaseSensitive = caseSensitive
  }

  /**
   * Copy struct (shallow copy)
   * @this {!JournalRequest}
   * @param {!JournalRequest} other Other struct
   * @returns {!JournalRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Module != null) {
      this.Module = other.Module
    } else {
      this.Module = null
    }
    if (other.LogLevel != null) {
      this.LogLevel = LogLevelType.fromObject(other.LogLevel)
    } else {
      this.LogLevel = null
    }
    if (other.TimeFrom != null) {
      if (other.TimeFrom instanceof Date) {
        this.TimeFrom = new Date(other.TimeFrom.getTime())
      } else {
        this.TimeFrom = new Date(Math.round(other.TimeFrom / 1000000))
      }
    } else {
      this.TimeFrom = null
    }
    if (other.TimeTo != null) {
      if (other.TimeTo instanceof Date) {
        this.TimeTo = new Date(other.TimeTo.getTime())
      } else {
        this.TimeTo = new Date(Math.round(other.TimeTo / 1000000))
      }
    } else {
      this.TimeTo = null
    }
    if (other.SearchText != null) {
      this.SearchText = other.SearchText
    } else {
      this.SearchText = null
    }
    if (other.CaseSensitive != null) {
      this.CaseSensitive = other.CaseSensitive
    } else {
      this.CaseSensitive = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!JournalRequest}
   * @returns {!JournalRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new JournalRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new JournalRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!JournalRequest}
   * @param {!JournalRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof JournalRequest)) {
      throw new TypeError('Instance of JournalRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!JournalRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Module: ((this.Module != null) ? this.Module : null),
      LogLevel: ((this.LogLevel != null) ? this.LogLevel : null),
      TimeFrom: ((this.TimeFrom != null) ? (this.TimeFrom.getTime() * 1000000) : null),
      TimeTo: ((this.TimeTo != null) ? (this.TimeTo.getTime() * 1000000) : null),
      SearchText: ((this.SearchText != null) ? this.SearchText : null),
      CaseSensitive: ((this.CaseSensitive != null) ? this.CaseSensitive : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return JournalRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!JournalRequest} other Object value
   * @returns {!JournalRequest} Created struct
   */
  static fromObject (other) {
    return new JournalRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!JournalRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return JournalRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!JournalRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6447
  }
}

export { JournalRequest };

/**
 * Fast Binary Encoding JournalRequest field model
 */
class FieldModelJournalRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.Module = new fbe.FieldModelString(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.LogLevel = new FieldModelLogLevelType(buffer, this.Module.fbeOffset + this.Module.fbeSize)
    this.TimeFrom = new fbe.FieldModelTimestamp(buffer, this.LogLevel.fbeOffset + this.LogLevel.fbeSize)
    this.TimeTo = new fbe.FieldModelTimestamp(buffer, this.TimeFrom.fbeOffset + this.TimeFrom.fbeSize)
    this.SearchText = new fbe.FieldModelString(buffer, this.TimeTo.fbeOffset + this.TimeTo.fbeSize)
    this.CaseSensitive = new fbe.FieldModelBool(buffer, this.SearchText.fbeOffset + this.SearchText.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelJournalRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelJournalRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelJournalRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Module.fbeSize + this.LogLevel.fbeSize + this.TimeFrom.fbeSize + this.TimeTo.fbeSize + this.SearchText.fbeSize + this.CaseSensitive.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelJournalRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Module.fbeExtra + this.LogLevel.fbeExtra + this.TimeFrom.fbeExtra + this.TimeTo.fbeExtra + this.SearchText.fbeExtra + this.CaseSensitive.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelJournalRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelJournalRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelJournalRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6447
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelJournalRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelJournalRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Module.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Module.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Module.fbeSize

    if ((fbeCurrentSize + this.LogLevel.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LogLevel.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogLevel.fbeSize

    if ((fbeCurrentSize + this.TimeFrom.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TimeFrom.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeFrom.fbeSize

    if ((fbeCurrentSize + this.TimeTo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TimeTo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeTo.fbeSize

    if ((fbeCurrentSize + this.SearchText.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SearchText.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SearchText.fbeSize

    if ((fbeCurrentSize + this.CaseSensitive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CaseSensitive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CaseSensitive.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelJournalRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelJournalRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelJournalRequest}
   * @param {!JournalRequest} fbeValue Default value, defaults is new JournalRequest()
   * @returns {!JournalRequest} JournalRequest value
   */
  get (fbeValue = new JournalRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelJournalRequest}
   * @param {!JournalRequest} fbeValue JournalRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Module.fbeSize) <= fbeStructSize) {
      fbeValue.Module = this.Module.get()
    } else {
      fbeValue.Module = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Module.fbeSize

    if ((fbeCurrentSize + this.LogLevel.fbeSize) <= fbeStructSize) {
      fbeValue.LogLevel = this.LogLevel.get(new LogLevelType(LogLevelType.ALL))
    } else {
      fbeValue.LogLevel = new LogLevelType(LogLevelType.ALL)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogLevel.fbeSize

    if ((fbeCurrentSize + this.TimeFrom.fbeSize) <= fbeStructSize) {
      fbeValue.TimeFrom = this.TimeFrom.get()
    } else {
      fbeValue.TimeFrom = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeFrom.fbeSize

    if ((fbeCurrentSize + this.TimeTo.fbeSize) <= fbeStructSize) {
      fbeValue.TimeTo = this.TimeTo.get()
    } else {
      fbeValue.TimeTo = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeTo.fbeSize

    if ((fbeCurrentSize + this.SearchText.fbeSize) <= fbeStructSize) {
      fbeValue.SearchText = this.SearchText.get()
    } else {
      fbeValue.SearchText = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SearchText.fbeSize

    if ((fbeCurrentSize + this.CaseSensitive.fbeSize) <= fbeStructSize) {
      fbeValue.CaseSensitive = this.CaseSensitive.get(false)
    } else {
      fbeValue.CaseSensitive = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CaseSensitive.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelJournalRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelJournalRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelJournalRequest}
   * @param {!JournalRequest} fbeValue JournalRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelJournalRequest}
   * @param {JournalRequest} fbeValue JournalRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Module.set(fbeValue.Module)
    this.LogLevel.set(fbeValue.LogLevel)
    this.TimeFrom.set(fbeValue.TimeFrom)
    this.TimeTo.set(fbeValue.TimeTo)
    this.SearchText.set(fbeValue.SearchText)
    this.CaseSensitive.set(fbeValue.CaseSensitive)
  }
}

export { FieldModelJournalRequest };

/**
 * Fast Binary Encoding JournalRequest model
 */
class JournalRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelJournalRequest(this.buffer, 4)
  }

  /**
   * Get the JournalRequest model
   * @this {!JournalRequestModel}
   * @returns {!FieldModelJournalRequest} model JournalRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!JournalRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!JournalRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return JournalRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!JournalRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelJournalRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!JournalRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!JournalRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!JournalRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!JournalRequestModel}
   * @param {!JournalRequest} value JournalRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!JournalRequestModel}
   * @param {!JournalRequest} value JournalRequest value, defaults is new JournalRequest()
   * @return {!object} Deserialized JournalRequest value and its size
   */
  deserialize (value = new JournalRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new JournalRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new JournalRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!JournalRequestModel}
   * @param {!number} prev Previous JournalRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { JournalRequestModel };
JournalRequest.__has_response = true;
JournalRequest.__response_class = JournalResponse;
